import {
  Component, Input, OnInit, OnChanges, OnDestroy, AfterViewInit, Output, EventEmitter,
  ViewChild, ElementRef, SimpleChanges, HostListener, NgZone, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { SeatBlock, SeatMapConfig, TicketTypeData } from '../../home/home.page';

interface Seat {
  id: string;
  blockName: string;
  row: number | string;
  col: number;
  number: number;
  x: number;
  y: number;
  status: 'available' | 'selected' | 'sold' | 'unavailable';
  ticketTypeId: string;
  priceTag?: string;
  floor: number;
  isDisabledAccessible?: boolean;
}

@Component({
  selector: 'app-seat-map-viewer',
  templateUrl: './seat-map-viewer.component.html',
  styleUrls: ['./seat-map-viewer.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule]
})
export class SeatMapViewerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() config?: SeatMapConfig;
  @Input() ticketTypes: TicketTypeData[] = [];
  @Input() soldSeats: string[] = [];
  @Input() unavailableTicketTypeIds: string[] = [];
  @Input() selectedLanguage: string = 'fr';
  @Input() initialSelectedSeats: any[] = [];
  // Highlight all categories of an offer; other seats are dimmed.
  @Input() highlightedTicketTypeIds: string[] = [];
  @Input() highlightTitle: string = '';
  @Input() highlightCaption: string = '';
  @Input() highlightCaptionItems: string[] = [];
  @Input() highlightApplied: boolean = false;
  @Input() highlightCloseLabel: string = '';
  @Output() seatSelected = new EventEmitter<Seat[]>();
  @Output() highlightCleared = new EventEmitter<void>();
  // Legend click: ticket type to highlight, or null to clear the highlight
  @Output() highlightRequested = new EventEmitter<string | null>();
  private highlightedTicketTypeIdSet = new Set<string>();

  @ViewChild('svgEl', { static: false }) svgEl?: ElementRef<SVGSVGElement>;
  @ViewChild('containerEl', { static: false }) containerEl?: ElementRef<HTMLElement>;

  // The legend items are created/destroyed with the legend: (re)bind the custom scrollbar each time
  @ViewChild('legendItemsEl', { static: false }) set legendItemsEl(ref: ElementRef<HTMLElement> | undefined) {
    this.bindLegendScrollbar(ref?.nativeElement);
  }
  private legendScrollbarCleanup: (() => void) | null = null;

  public selectedSeats: Map<string, Seat> = new Map();
  public selectedFloors: Set<number> = new Set();
  public viewBox: string = '0 0 1000 1000';

  public hoveredSeat: Seat | null = null;
  public hoveredTicketType: TicketTypeData | null = null;
  public tooltipPosition = { x: 0, y: 0 };
  public tooltipAtBottom = false;

  // Viewport state (in SVG coordinate space)
  private _vx = 0;   // viewBox origin X
  private _vy = 0;   // viewBox origin Y
  private _vw = 1000; // viewBox width
  private _vh = 1000; // viewBox height

  // Natural (fit-all) bounds
  private _naturalX = 0;
  private _naturalY = 0;
  private _naturalW = 1000;
  private _naturalH = 1000;
  private backgroundImageAspect: { uri: string; aspect: number | null } | null = null;
  private containerResizeObserver: ResizeObserver | null = null;

  // Pan state
  private _isPanning = false;
  private _panStartClient = { x: 0, y: 0 };
  private _panStartOrigin = { x: 0, y: 0 };
  private hasUserInteracted = false;
  private lastTouchDist = 0;

  // Pinch zoom state
  private _lastPinchDist = 0;

  private highlightAnimationFrame: number | null = null;

  public isLegendOpen = true;
  public legendTicketTypes: TicketTypeData[] = [];
  private legendKey = '';

  private blockSeatsCache: Map<string, Seat[]> = new Map();
  private seatByIdCache: Map<string, Seat> = new Map();

  constructor(private ngZone: NgZone, public cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.recalcBounds();
    this.initializeSelectedSeats();
    this.selectAllFloors();
  }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      this.setupNativeListeners();
    });
    // Re-calculate bounds after view init to ensure SVG dimensions are ready
    setTimeout(() => {
      this.recalcBounds();
      this.commitViewBox();
    }, 100);
  }

  private setupNativeListeners() {
    const container = this.containerEl?.nativeElement;
    if (!container) return;

    // Refit the default view to the canvas whenever its size changes (layout, rotation...)
    this.containerResizeObserver = new ResizeObserver(() => this.recalcBounds());
    this.containerResizeObserver.observe(container);

    // Wheel listener (Desktop/Trackpad)
    container.addEventListener('wheel', (event: any) => {
      // Let the legend scroll natively
      if ((event.target as HTMLElement).closest('.seat-legend')) return;

      event.preventDefault();
      const dy = event.deltaY || 0;
      if (Math.abs(dy) < 0.1) return;

      const zoomSensitivity = 0.0015;
      const factor = Math.max(0.8, Math.min(1.2, 1 + dy * zoomSensitivity));
      this.zoomAroundClientPoint(event.clientX, event.clientY, factor);
    }, { passive: false });

    // Mouse Pan (Desktop)
    container.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;

      // Don't start a pan when the press lands on a seat or a button —
      // let the container's (click) handler / the button's own handler take it.
      if (target.closest('.seat') || target.closest('button') || target.closest('.seat-legend')) {
        this._isPanning = false;
        return;
      }

      this._isPanning = true;
      this._panStartClient = { x: e.clientX, y: e.clientY };
      this._panStartOrigin = { x: this._vx, y: this._vy };
    });

    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (!this._isPanning) return;
      const svg = this.svgEl?.nativeElement;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const dx = (e.clientX - this._panStartClient.x) * (this._vw / rect.width);
      const dy = (e.clientY - this._panStartClient.y) * (this._vh / rect.height);
      this._vx = this._panStartOrigin.x - dx;
      this._vy = this._panStartOrigin.y - dy;
      this.hasUserInteracted = true;
      this.commitViewBox();
    });

    document.addEventListener('mouseup', () => {
      this._isPanning = false;
    });

    // Touch listeners (Mobile)
    container.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const target = e.target as HTMLElement;
        if (target.closest('.seat') || target.closest('.seat-legend')) return;

        this._isPanning = true;
        this._panStartClient = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        this._panStartOrigin = { x: this._vx, y: this._vy };
      } else if (e.touches.length === 2) {
        this._isPanning = false;
        this.lastTouchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    }, { passive: false });

    container.addEventListener('touchmove', (e: TouchEvent) => this.onTouchMove(e), { passive: false });

    container.addEventListener('touchend', () => {
      this._isPanning = false;
    }, { passive: false });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      this.blockSeatsCache.clear();
      this.seatByIdCache.clear();
      this.recalcBounds();
      this.selectAllFloors();
    }
    if (changes['initialSelectedSeats']) {
      this.initializeSelectedSeats();
    }
    if (changes['config'] || changes['ticketTypes']) {
      this.updateLegendTicketTypes();
    }
    if (changes['highlightedTicketTypeIds']) {
      this.highlightedTicketTypeIdSet = new Set(this.highlightedTicketTypeIds);
      if (this.highlightedTicketTypeIds.length)
        this.focusOnTicketTypes(this.highlightedTicketTypeIds);
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Legend
  // ────────────────────────────────────────────────────────────────

  /** Keeps the ticket types placed on the map, rebuilt only when they change ([config] is a new object on every parent check). */
  private updateLegendTicketTypes() {
    const ticketTypeIds = new Set((this.config?.blocks || []).map(block => block.ticketTypeId));
    const legendTicketTypes = this.ticketTypes.filter(ticketType => ticketTypeIds.has(ticketType.id));
    const key = legendTicketTypes.map(ticketType => ticketType.id).join('|');
    if (key !== this.legendKey || legendTicketTypes.some((ticketType, index) => ticketType !== this.legendTicketTypes[index])) {
      this.legendKey = key;
      this.legendTicketTypes = legendTicketTypes;
    }
  }

  hasLegendPrice(ticketType: TicketTypeData): boolean {
    return Number.isFinite(ticketType.lowestPrice) && Number.isFinite(ticketType.highestPrice);
  }

  trackByTicketTypeId(_index: number, ticketType: TicketTypeData): string {
    return ticketType.id;
  }

  /** Keeps the custom legend scrollbar thumb in sync with the items scroll, outside Angular (direct DOM updates). */
  private bindLegendScrollbar(items: HTMLElement | undefined) {
    this.legendScrollbarCleanup?.();
    this.legendScrollbarCleanup = null;

    const track = items?.parentElement?.querySelector<HTMLElement>('.seat-legend-scrollbar');
    const thumb = track?.querySelector<HTMLElement>('.seat-legend-scrollbar-thumb');
    if (!items || !track || !thumb) return;

    const update = () => {
      const { scrollHeight, clientHeight, scrollTop } = items;
      const overflows = scrollHeight > clientHeight + 1;
      track.classList.toggle('visible', overflows);
      if (!overflows) return;

      const trackHeight = track.clientHeight;
      const thumbHeight = Math.max(24, trackHeight * clientHeight / scrollHeight);
      const thumbTop = (trackHeight - thumbHeight) * scrollTop / (scrollHeight - clientHeight);
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${thumbTop}px)`;
    };

    this.ngZone.runOutsideAngular(() => {
      items.addEventListener('scroll', update, { passive: true });
      const resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(items);
      // Items resize when their text changes (e.g. language switch), which changes the scroll height
      Array.from(items.children).forEach(child => resizeObserver.observe(child));
      const frame = requestAnimationFrame(update);
      this.legendScrollbarCleanup = () => {
        items.removeEventListener('scroll', update);
        resizeObserver.disconnect();
        cancelAnimationFrame(frame);
      };
    });
  }

  ngOnDestroy() {
    this.legendScrollbarCleanup?.();
    this.containerResizeObserver?.disconnect();
  }

  toggleLegend(event: Event) {
    event.stopPropagation();
    this.isLegendOpen = !this.isLegendOpen;
  }

  onLegendTicketTypeClick(event: Event, ticketTypeId: string) {
    event.stopPropagation();
    this.highlightRequested.emit(this.highlightedTicketTypeIds.length === 1 && this.isTicketTypeHighlighted(ticketTypeId) ? null : ticketTypeId);
  }

  // ────────────────────────────────────────────────────────────────
  // Ticket type highlight
  // ────────────────────────────────────────────────────────────────

  /** Checks category membership without scanning the highlighted group for every rendered seat. */
  isTicketTypeHighlighted(ticketTypeId: string): boolean {
    return this.highlightedTicketTypeIdSet.has(ticketTypeId);
  }

  /** Supplies the category label and color for an individual highlight or the first group member. */
  get highlightedTicketType(): TicketTypeData | null {
    return this.ticketTypes.find(t => this.isTicketTypeHighlighted(t.id)) || null;
  }

  clearHighlight(event?: Event) {
    event?.stopPropagation();
    this.highlightCleared.emit();
  }

  /** Shows the floors holding the highlighted categories, then zooms to fit all their seats. */
  focusOnTicketTypes(ticketTypeIds: string[]) {
    const ids = new Set(ticketTypeIds);
    const blocks = (this.config?.blocks || []).filter(block => ids.has(block.ticketTypeId));
    if (blocks.length === 0) return;

    if (this.config?.hasFloors) {
      const blockFloors = new Set(blocks.map(block => block.floor ?? 0));
      if (![...blockFloors].every(floor => this.selectedFloors.has(floor)))
        this.selectAllFloors();
    }

    // Seat bounding box in global coordinates (block translate + rotate)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const block of blocks) {
      const angle = ((block.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(angle), sin = Math.sin(angle);
      const r = (block as any).seatSize || 8;
      for (const seat of this.getSeatsForBlock(block)) {
        const gx = block.x + seat.x * cos - seat.y * sin;
        const gy = block.y + seat.x * sin + seat.y * cos;
        minX = Math.min(minX, gx - r); minY = Math.min(minY, gy - r);
        maxX = Math.max(maxX, gx + r); maxY = Math.max(maxY, gy + r);
      }
    }
    if (minX === Infinity) return;

    // Keep the natural aspect ratio, add some breathing room and respect the zoom limits
    const PAD = 60;
    const ratio = this._naturalW / this._naturalH;
    let targetW = Math.max(maxX - minX + PAD * 2, (maxY - minY + PAD * 2) * ratio);
    targetW = Math.min(this._naturalW, Math.max(this._naturalW * 0.05, targetW));
    const targetH = targetW / ratio;
    const targetX = (minX + maxX) / 2 - targetW / 2;
    const targetY = (minY + maxY) / 2 - targetH / 2;

    this.hasUserInteracted = true;
    this.animateViewBox(targetX, targetY, targetW, targetH, 650);
  }

  private animateViewBox(toX: number, toY: number, toW: number, toH: number, duration: number) {
    if (this.highlightAnimationFrame !== null)
      cancelAnimationFrame(this.highlightAnimationFrame);

    const from = { x: this._vx, y: this._vy, w: this._vw, h: this._vh };
    const start = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    this.ngZone.runOutsideAngular(() => {
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const k = easeOutCubic(t);
        this._vx = from.x + (toX - from.x) * k;
        this._vy = from.y + (toY - from.y) * k;
        this._vw = from.w + (toW - from.w) * k;
        this._vh = from.h + (toH - from.h) * k;
        this.commitViewBox();
        this.highlightAnimationFrame = t < 1 ? requestAnimationFrame(step) : null;
      };
      this.highlightAnimationFrame = requestAnimationFrame(step);
    });
  }

  private initializeSelectedSeats() {
    this.selectedSeats.clear();
    if (this.initialSelectedSeats && this.initialSelectedSeats.length > 0) {
      this.initialSelectedSeats.forEach(seat => {
        this.selectedSeats.set(seat.id, seat);
      });
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Bounds & ViewBox
  // ────────────────────────────────────────────────────────────────

  private recalcBounds() {
    if (!this.config?.blocks?.length) {
      this.setNatural(0, 0, 1000, 1000);
      return;
    }

    const PAD = 16;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const block of this.config.blocks) {
      const seats = this.getSeatsForBlock(block);
      for (const seat of seats) {
        // The seat position in global coords depends on block transform.
        // We approximate by using the block's x/y offset and the local seat coords.
        // (No rotation math for simplicity — rotated blocks are edge cases.)
        const gx = block.x + seat.x;
        const gy = block.y + seat.y;
        const r = (block as any).seatSize || 8;
        if (gx - r < minX) minX = gx - r;
        if (gy - r < minY) minY = gy - r;
        if (gx + r > maxX) maxX = gx + r;
        if (gy + r > maxY) maxY = gy + r;
      }
    }

    if (this.config.backgroundImageURI) {
      const imgX = this.config.backgroundImageX || 0;
      const imgY = this.config.backgroundImageY || 0;
      const box = 1000 * (this.config.backgroundImageScale || 1);
      // The <image> is drawn "meet" inside a square box: use the picture's real
      // rectangle so the empty bands of the box don't count as map content.
      const aspect = this.getBackgroundImageAspect(this.config.backgroundImageURI);
      const drawnW = aspect && aspect < 1 ? box * aspect : box;
      const drawnH = aspect && aspect > 1 ? box / aspect : box;
      const drawnX = imgX + (box - drawnW) / 2;
      const drawnY = imgY + (box - drawnH) / 2;
      if (drawnX < minX) minX = drawnX;
      if (drawnY < minY) minY = drawnY;
      if (drawnX + drawnW > maxX) maxX = drawnX + drawnW;
      if (drawnY + drawnH > maxY) maxY = drawnY + drawnH;
    }

    if (minX === Infinity || isNaN(minX)) {
      this.setNatural(0, 0, 1000, 1000);
      return;
    }

    // Only reset the view if the user hasn't started interacting with the map
    if (!this.hasUserInteracted) {
      this.setNaturalFitted(minX - PAD, minY - PAD, maxX - minX + PAD * 2, maxY - minY + PAD * 2);
    }
  }

  /**
   * Widens the content bounds to the container's aspect ratio so the default view fills the
   * whole canvas (no letterboxing) and panning/zoom limits match what is actually visible.
   */
  private setNaturalFitted(x: number, y: number, w: number, h: number) {
    const rect = this.containerEl?.nativeElement.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      const ratio = rect.width / rect.height;
      if (w / h < ratio) {
        const newW = h * ratio;
        x -= (newW - w) / 2;
        w = newW;
      } else {
        const newH = w / ratio;
        y -= (newH - h) / 2;
        h = newH;
      }
    }
    this.setNatural(x, y, w, h);
  }

  /** Width / height of the background picture, loaded once per URI (null until known). */
  private getBackgroundImageAspect(uri: string): number | null {
    if (this.backgroundImageAspect?.uri === uri) return this.backgroundImageAspect.aspect;
    this.backgroundImageAspect = { uri, aspect: null };
    const img = new Image();
    img.onload = () => {
      if (this.backgroundImageAspect?.uri !== uri || !img.naturalWidth || !img.naturalHeight) return;
      this.backgroundImageAspect.aspect = img.naturalWidth / img.naturalHeight;
      this.recalcBounds();
      this.cdr.markForCheck();
    };
    img.src = uri;
    return null;
  }

  private setNatural(x: number, y: number, w: number, h: number) {
    this._naturalX = x;
    this._naturalY = y;
    this._naturalW = w;
    this._naturalH = h;
    // Reset to fit-all view
    this._vx = x;
    this._vy = y;
    this._vw = w;
    this._vh = h;
    this.commitViewBox();
  }

  // Keep the viewport within the content's natural bounds so the map can never be
  // panned/zoomed completely out of view. _vw/_vh are always <= natural W/H (zoom is
  // capped at the natural size), so the content always has room to fully cover the viewport.
  private clampViewport() {
    const maxVx = this._naturalX + Math.max(0, this._naturalW - this._vw);
    const maxVy = this._naturalY + Math.max(0, this._naturalH - this._vh);
    this._vx = Math.min(Math.max(this._vx, this._naturalX), maxVx);
    this._vy = Math.min(Math.max(this._vy, this._naturalY), maxVy);
  }

  private commitViewBox() {
    this.clampViewport();
    this.viewBox = `${this._vx} ${this._vy} ${this._vw} ${this._vh}`;
    // Force attribute update on the native element to bypass any Angular change detection lag
    // or potential case-sensitivity issues with 'viewBox'
    if (this.svgEl?.nativeElement) {
      this.svgEl.nativeElement.setAttribute('viewBox', this.viewBox);
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Zoom (wheel + pinch)
  // ────────────────────────────────────────────────────────────────

  private zoomAroundClientPoint(clientX: number, clientY: number, factor: number) {
    const svg = this.svgEl?.nativeElement;
    if (!svg) return;

    const pt = this.clientToSVG(svg, clientX, clientY);

    let finalFactor = factor;
    let newW = this._vw * factor;

    const minW = this._naturalW * 0.05;
    const maxW = this._naturalW; // Don't zoom out beyond natural (initial) bounds

    if (newW > maxW) {
      finalFactor = maxW / this._vw;
      newW = maxW;
    } else if (newW < minW) {
      finalFactor = minW / this._vw;
      newW = minW;
    }

    if (Math.abs(finalFactor - 1) < 0.0001) return;

    const newH = this._vh * finalFactor;

    // Keep the SVG point under the pointer fixed
    this._vx = pt.x - (pt.x - this._vx) * finalFactor;
    this._vy = pt.y - (pt.y - this._vy) * finalFactor;
    this._vw = newW;
    this._vh = newH;
    this.hasUserInteracted = true;
    this.commitViewBox();
  }

  // ────────────────────────────────────────────────────────────────
  // Pan (mouse drag)
  // ────────────────────────────────────────────────────────────────

  // Mouse Pan/Zoom handled natively outside the zone for performance

  // ────────────────────────────────────────────────────────────────
  // Touch Pan + Pinch Zoom
  // ────────────────────────────────────────────────────────────────

  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 1) {
      this._isPanning = true;
      this._panStartClient = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      this._panStartOrigin = { x: this._vx, y: this._vy };
    } else if (event.touches.length === 2) {
      this._isPanning = false;
      this.lastTouchDist = Math.hypot(
        event.touches[0].clientX - event.touches[1].clientX,
        event.touches[0].clientY - event.touches[1].clientY
      );
    }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 2) {
      // Pinch zoom
      event.preventDefault();
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);

      if (this.lastTouchDist > 0) {
        const factor = this.lastTouchDist / dist; // Inverted because factor > 1 zooms OUT
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        this.hasUserInteracted = true;
        this.zoomAroundClientPoint(centerX, centerY, factor);
      }
      this.lastTouchDist = dist;
    } else if (event.touches.length === 1 && this._isPanning) {
      const svg = this.svgEl?.nativeElement;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const scaleX = this._vw / rect.width;
      const scaleY = this._vh / rect.height;
      const dx = (event.touches[0].clientX - this._panStartClient.x) * scaleX;
      const dy = (event.touches[0].clientY - this._panStartClient.y) * scaleY;
      this._vx = this._panStartOrigin.x - dx;
      this._vy = this._panStartOrigin.y - dy;
      this.hasUserInteracted = true;
      this.commitViewBox();
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (event.touches.length === 0) this._isPanning = false;
  }

  private pinchDist(event: TouchEvent): number {
    const dx = event.touches[0].clientX - event.touches[1].clientX;
    const dy = event.touches[0].clientY - event.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Reset zoom/pan to fit-all
  resetView() {
    // Same smooth zoom as the ticket type focus
    this.animateViewBox(this._naturalX, this._naturalY, this._naturalW, this._naturalH, 650);
  }

  // ────────────────────────────────────────────────────────────────
  // Tooltip
  // ────────────────────────────────────────────────────────────────

  onSeatMouseEnter(event: MouseEvent, seat: Seat) {
    const status = this.getSeatStatus(seat.id);
    if (status === 'sold' || status === 'unavailable' || !this.isSeatOnSelectedFloor(seat)) return;
    this.hoveredSeat = seat;
    this.hoveredTicketType = this.ticketTypes.find(t => t.id === seat.ticketTypeId) || null;
    this.updateTooltipPosition(event);
  }

  onSeatMouseMove(event: MouseEvent) {
    if (this.hoveredSeat) this.updateTooltipPosition(event);
  }

  onSeatMouseLeave() {
    this.hoveredSeat = null;
    this.hoveredTicketType = null;
  }

  // Hide popup while panning; actual hover/unhover is driven by the seat elements'
  // own (mouseenter)/(mouseleave) native events, which are accurate at any zoom level.
  onContainerMouseMove(event: MouseEvent) {
    if (this._isPanning) {
      this.hoveredSeat = null;
      this.hoveredTicketType = null;
    }
  }

  // Mouse click-to-select relies on hoveredSeat (kept accurate by the seats' own
  // (mouseenter)/(mousemove) bindings) rather than a (mousedown)/(click) handler
  // directly on the seat shape, since those are unreliable through the native
  // pan-detection layer on some platforms.
  onContainerClick(event: MouseEvent) {
    if (!this.hoveredSeat) return;
    this.toggleSeat(event, this.hoveredSeat);
  }

  private updateTooltipPosition(event: MouseEvent) {
    const container = (event.target as HTMLElement).closest('.viewer-container');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top - 10;
    const tooltipWidth = 180;
    const padding = 10;
    let adjustedX = x;
    if (x < (tooltipWidth / 2) + padding) adjustedX = (tooltipWidth / 2) + padding;
    else if (x > rect.width - (tooltipWidth / 2) - padding) adjustedX = rect.width - (tooltipWidth / 2) - padding;
    const tooltipHeight = 100;
    this.tooltipAtBottom = y < tooltipHeight + padding;
    this.tooltipPosition = { x: adjustedX, y };
  }

  // ────────────────────────────────────────────────────────────────
  // Seats
  // ────────────────────────────────────────────────────────────────

  // Keep the seat/block DOM nodes stable across change-detection cycles even though
  // [config] (bound to a getter on the parent) hands us a new object/array on every
  // pass — without this, *ngFor destroys and recreates the seat elements on every
  // hover-triggered CD tick, which breaks the browser's mousedown/mouseup → click
  // synthesis (the element under the pointer gets swapped out mid-click).
  trackByBlockId(_index: number, block: SeatBlock): string {
    return block.id;
  }

  trackBySeatId(_index: number, seat: Seat): string {
    return seat.id;
  }

  getSeatsForBlock(block: SeatBlock): Seat[] {
    if (this.blockSeatsCache.has(block.id)) return this.blockSeatsCache.get(block.id)!;
    const seats: Seat[] = [];
    let seatNumber = block.firstSeatNumber || 1;
    const curvature = block.curvature || 0;
    for (let r = 0; r < block.rows; r++) {
      const rowLabel = getRowLabel(block.firstRowNumber, r);
      for (let s = 0; s < block.seatsPerRow; s++) {
        const midSeat = (block.seatsPerRow - 1) / 2;
        const curveOffset = curvature * Math.pow(s - midSeat, 2) * 0.1;

        seats.push({
          id: `${block.id}-${r}-${s}`,
          blockName: block.name,
          row: rowLabel,
          col: s + 1,
          number: seatNumber++,
          x: s * block.horizontalSpacing + block.horizontalSpacing / 2,
          y: r * block.verticalSpacing + block.verticalSpacing / 2 + curveOffset,
          status: 'available',
          ticketTypeId: block.ticketTypeId,
          floor: block.floor ?? 0,
          isDisabledAccessible: block.isDisabledAccessible
        });
      }
    }
    this.blockSeatsCache.set(block.id, seats);
    for (const seat of seats) this.seatByIdCache.set(seat.id, seat);
    return seats;
  }

  getSeatStatus(id: string): 'available' | 'selected' | 'sold' | 'unavailable' {
    if (this.soldSeats.includes(id)) return 'sold';
    if (this.selectedSeats.has(id)) return 'selected';
    const seat = this.findSeatById(id);
    if (seat && this.unavailableTicketTypeIds.includes(seat.ticketTypeId)) return 'unavailable';
    return 'available';
  }

  private findSeatById(id: string): Seat | null {
    return this.seatByIdCache.get(id) || null;
  }

  // ────────────────────────────────────────────────────────────────
  // Floors
  // ────────────────────────────────────────────────────────────────

  get floors(): number[] {
    if (!this.config?.hasFloors || !this.config?.floorsCount) return [];
    return Array.from({ length: this.config.floorsCount + 1 }, (_, i) => i);
  }

  toggleFloor(floor: number) {
    this.selectedFloors = new Set([floor]);
  }

  selectAllFloors() {
    this.selectedFloors = new Set(this.floors);
  }

  isFloorSelected(floor: number): boolean {
    if (this.selectedFloors.size === this.floors.length) return false;
    return this.selectedFloors.has(floor);
  }

  isSeatOnSelectedFloor(seat: Seat): boolean {
    return !this.config?.hasFloors || this.selectedFloors.has(seat.floor);
  }

  private toggleSeatById(id: string) {
    const foundSeat = this.findSeatById(id);
    if (foundSeat) this.toggleSeat(null as any, foundSeat);
  }

  toggleSeat(event: Event, seat: Seat) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.ngZone.run(() => {
      const status = this.getSeatStatus(seat.id);
      if (status === 'sold' || status === 'unavailable' || !this.isSeatOnSelectedFloor(seat)) return;

      if (this.selectedSeats.has(seat.id)) {
        this.selectedSeats.delete(seat.id);
      } else {
        this.selectedSeats.set(seat.id, seat);
      }

      this.seatSelected.emit(Array.from(this.selectedSeats.values()));
      this.cdr.detectChanges();
    });
  }

  getSeatColor(seat: Seat): string {
    if (!this.isSeatOnSelectedFloor(seat)) return '#aaa';
    const status = this.getSeatStatus(seat.id);
    if (status === 'selected') return '#FFF';
    if (status === 'sold' || status === 'unavailable') return '#777';
    return this.getTicketTypeColor(seat.ticketTypeId);
  }

  getTicketTypeColor(ticketTypeId: string): string {
    const ticketType = this.ticketTypes.find(t => t.id === ticketTypeId);
    return ticketType?.color || '#000000';
  }

  // ────────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────────

  private clientToSVG(svg: SVGSVGElement, clientX: number, clientY: number): { x: number, y: number } {
    const rect = svg.getBoundingClientRect();
    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;
    return { x: this._vx + nx * this._vw, y: this._vy + ny * this._vh };
  }

  get isPanning(): boolean { return this._isPanning; }
}

function letterToNumber(str: string): number {
  let num = 0;
  const upper = str.toUpperCase();
  for (let i = 0; i < upper.length; i++) {
    num = num * 26 + (upper.charCodeAt(i) - 64);
  }
  return num - 1;
}

function indexToLetter(num: number): string {
  let letter = '';
  let n = num + 1;
  while (n > 0) {
    let rem = (n - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    n = Math.floor((n - 1) / 26);
  }
  return letter;
}

function getRowLabel(firstRow: number | string | undefined, r: number): number | string {
  if (firstRow === undefined || firstRow === null || firstRow === '') {
    return 1 + r;
  }

  const str = String(firstRow).trim();
  if (!isNaN(Number(str))) {
    return Number(str) + r;
  }

  if (/^[a-zA-Z]+$/.test(str)) {
    const isLower = str === str.toLowerCase();
    const l = indexToLetter(letterToNumber(str) + r);
    return isLower ? l.toLowerCase() : l;
  }

  return `${str}${r > 0 ? ' (' + (r + 1) + ')' : ''}`;
}
